import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Layers,
  FileText,
  Settings as SettingsIcon,
  CreditCard,
  PuzzleIcon,
  LifeBuoy,
  Building2,
  ShieldAlert,
  Bell,
  LogOut,
  ChevronRight,
  Sparkles,
  Globe,
} from "lucide-react";
import RefocusWidgetDemo from "@/components/app/RefocusWidgetDemo";

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showWidgetDemo, setShowWidgetDemo] = useState(false);

  const baseLinks = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/hierarchy", label: "Hierarchy", icon: Layers },
    { to: "/app/reports", label: "Reports", icon: FileText },
    { to: "/app/settings", label: "Settings", icon: SettingsIcon },
    { to: "/app/billing", label: "Billing", icon: CreditCard },
    { to: "/app/integrations", label: "Integrations", icon: PuzzleIcon },
    { to: "/app/extension", label: "Browser extension", icon: Globe },
    { to: "/app/support", label: "Support", icon: LifeBuoy },
  ];
  const adminLinks = ["admin", "super_admin", "s_tier"].includes(user?.role)
    ? [{ to: "/app/workspace", label: "Workspace", icon: Building2 }]
    : [];
  const sTierLinks = user?.role === "s_tier"
    ? [{ to: "/app/admin", label: "Admin Control", icon: ShieldAlert }]
    : [];

  return (
    <div className="min-h-screen bg-[#F8F9FC]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:flex-col w-64 min-h-screen border-r border-[#E8ECF2] bg-white sticky top-0" data-testid="app-sidebar">
          <Link to="/app" className="flex items-center gap-2 px-6 h-16 border-b border-[#E8ECF2]">
            <div className="h-8 w-8 rounded-md bg-[#1A3A5C] flex items-center justify-center">
              <span className="font-display font-bold text-[#F8F9FC] text-sm">W</span>
            </div>
            <span className="font-display text-xl font-bold tracking-tighter text-[#1A1A2E]">wylo</span>
          </Link>
          <nav className="flex-1 p-4 space-y-1">
            {[...baseLinks, ...adminLinks, ...sTierLinks].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                    isActive ? "bg-[#1A3A5C] text-white" : "text-[#1A1A2E] hover:bg-[#E8ECF2]"
                  }`
                }
                data-testid={`sidebar-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <l.icon size={16} />
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-[#E8ECF2]">
            <div className="rounded-md bg-gradient-to-br from-[#1A3A5C] to-[#2C6BAC] text-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} />
                <p className="text-xs uppercase tracking-[0.18em] font-semibold">Compass</p>
              </div>
              <p className="text-xs text-[#E8ECF2]/85 leading-relaxed">
                AI-enhanced reports & assistant. $9/user/mo.
              </p>
              <button className="mt-3 text-xs underline hover:text-white" onClick={() => nav("/app/billing")} data-testid="sidebar-compass-cta">
                Add Compass
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <header className="h-16 border-b border-[#E8ECF2] bg-white px-6 lg:px-8 flex items-center justify-between sticky top-0 z-30" data-testid="app-header">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#555568] font-mono">
                  {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                </p>
                <p className="font-display font-bold text-[#1A1A2E] text-base">
                  Welcome back, {user?.name?.split(" ")[0]}
                </p>
              </div>
              <span
                className="hidden md:inline-flex ml-3 text-[10px] uppercase tracking-[0.18em] bg-[#1A3A5C] text-white px-2 py-1 rounded font-semibold"
                data-testid="tier-badge"
              >
                WYLO {user?.tier || "Spark"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWidgetDemo(true)}
                className="hidden md:inline-flex border-[#1A3A5C] text-[#1A1A2E]"
                data-testid="header-refocus-demo"
              >
                Show refocus widget
              </Button>
              <Button variant="ghost" size="icon" className="text-[#555568]">
                <Bell size={18} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#E8ECF2]" data-testid="account-menu-trigger">
                    <div className="h-8 w-8 rounded-full bg-[#1A3A5C] text-white flex items-center justify-center text-xs font-bold">
                      {user?.name?.[0] || "W"}
                    </div>
                    <ChevronRight size={14} className="text-[#555568] rotate-90" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-[#555568] text-xs uppercase tracking-[0.18em]">Account</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => nav("/app/settings")}>Settings</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/billing")}>Billing</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="p-6 lg:p-10">
            <Outlet />
          </main>
        </div>
      </div>

      {showWidgetDemo && <RefocusWidgetDemo onClose={() => setShowWidgetDemo(false)} />}
    </div>
  );
}
